import { Text } from 'components';
import { theme } from 'lib';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiPlus } from 'react-icons/fi';
import { ANCHOR_EDGE, MATCH_EVENT_LABEL, anchorLabel, waveEdgesLabel } from '../anchors';
import { WAVE_STATUS, isTerminalStatus } from '../constants';
import { CONFLICT_KIND, phaseBands, phaseBoundaries, positionIn, segmentFill, snapToAnchor, timeAt } from '../timeline';
import { MATCH_ACCENT } from './ui';
import { fmtMaybe, fmtSpan, fmtTime } from './timelineUi';

// THE SESSION TRACK — every wave on one match, drawn as consecutive segments on a single row.
//
// One row, never stacked, and that is the design rather than a simplification: only one wave can
// hold a team's session at a time, so waves on a match are a SEQUENCE. Drawing them on one track
// makes overlap physically impossible to express, which replaces a validation error the admin hit
// after submitting with a shape they cannot draw in the first place.
//
// Observed vs projected runs through everything here. A phase boundary the match actually reached
// is a solid line; one still projected from kick-off is dashed. A `pending` wave's position WILL
// move as the match progresses — that is correct behaviour, not a glitch — so its segment carries
// the same dashed treatment and its times a "~".

const TRACK_HEIGHT = 46;
const STRIP_HEIGHT = 26;
// Snap radius in PIXELS, not minutes: the same gesture has to feel the same on a 90-minute axis
// and on a three-day one, so the tolerance is converted to minutes per render off the track width.
const SNAP_PX = 12;
const MS_PER_MIN = 60 * 1000;

/**
 * Segment paint by lifecycle status. `fill` comes from `segmentFill` (muted/filled/dashed).
 *
 * `pendingBorder` is the colour a DASHED edge is drawn in when the wave's position is still
 * projected, and it exists because the obvious choice fails: a filled segment's border matches its
 * own fill, so a dashed border on it is invisible — and a running wave is exactly the case where
 * "this position will still move" most needs saying. Each state therefore names a border that
 * contrasts with its own fill.
 */
const SEGMENT_PAINT = {
  filled: {
    bg: MATCH_ACCENT.rail,
    border: MATCH_ACCENT.rail,
    pendingBorder: '#fff',
    col: '#fff',
    dash: false
  },
  dashed: {
    bg: MATCH_ACCENT.chip,
    border: MATCH_ACCENT.rail,
    pendingBorder: MATCH_ACCENT.rail,
    col: '#1E3A8A',
    dash: true
  },
  muted: {
    bg: theme.GREY_10,
    border: theme.GREY_30,
    pendingBorder: theme.GREY_50,
    col: theme.GREY_60,
    dash: false
  }
};

const CONFLICT_PAINT = {
  [CONFLICT_KIND.BLOCKED]: { border: '#DC2626', bg: '#FEE2E2', col: '#991B1B' },
  [CONFLICT_KIND.OVERLAP]: { border: '#D97706', bg: '#FEF3C7', col: '#92400E' }
};

/**
 * The worst conflict on a segment decides its paint. `blocked` outranks `overlap`: a blocked wave
 * never opens at all, where an overlap is usually a fixture that moved under a wave that still
 * runs.
 */
const conflictPaintOf = (conflicts) => {
  if (!conflicts?.length) return null;
  const blocked = conflicts.find((c) => c.kind === CONFLICT_KIND.BLOCKED);
  const chosen = blocked || conflicts[0];
  return CONFLICT_PAINT[chosen.kind] || CONFLICT_PAINT[CONFLICT_KIND.OVERLAP];
};

const Handle = ({ side, onPointerDown, disabled, title }) => (
  <div
    onPointerDown={disabled ? undefined : onPointerDown}
    title={title}
    role={disabled ? undefined : 'slider'}
    aria-label={disabled ? undefined : `Drag the ${side} edge`}
    style={{
      position: 'absolute',
      top: 0,
      bottom: 0,
      [side === 'open' ? 'left' : 'right']: -4,
      width: 9,
      cursor: disabled ? 'default' : 'ew-resize',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      touchAction: 'none'
    }}
  >
    {!disabled && (
      <span
        style={{
          width: 3,
          height: 18,
          borderRadius: 2,
          background: '#fff',
          boxShadow: '0 0 0 1px rgba(15,23,42,0.35)'
        }}
      />
    )}
  </div>
);

const SessionTrack = ({
  timeline,
  window: axis,
  anchorsFor,
  selectedWaveId,
  onSelectWave,
  onAddInGap,
  onAddAtEnd,
  onDragEdge,
  readOnly
}) => {
  const trackRef = useRef(null);
  const [drag, setDrag] = useState(null);

  const bands = useMemo(() => phaseBands(timeline?.phases, axis), [timeline, axis]);
  const boundaries = useMemo(() => phaseBoundaries(timeline?.phases), [timeline]);

  const pctOf = useCallback((at) => positionIn(axis, at), [axis]);

  // Pixel snap radius → minutes, recomputed per drag so it tracks the axis span AND the rendered
  // width. Falls back to a sane 3 minutes before the first layout pass.
  const toleranceMinAt = useCallback(() => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect?.width || !axis?.from || !axis?.to) return 3;
    const spanMin = (axis.to.getTime() - axis.from.getTime()) / MS_PER_MIN;
    return (SNAP_PX / rect.width) * spanMin;
  }, [axis]);

  const pctFromEvent = useCallback((clientX) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect?.width) return 0;
    return Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
  }, []);

  // Pointer capture lives on `window` rather than the handle so a fast drag that leaves the track
  // still tracks, and a pointerup anywhere still commits.
  useEffect(() => {
    if (!drag) return undefined;

    const move = (e) => {
      const pct = pctFromEvent(e.clientX);
      const at = timeAt(axis, pct);
      const snapped = snapToAnchor(at, {
        phases: timeline?.phases,
        toleranceMin: toleranceMinAt()
      });
      setDrag((prev) => (prev ? { ...prev, pct, at, snapped } : prev));
    };

    const up = () => {
      setDrag((prev) => {
        if (prev?.snapped && onDragEdge) onDragEdge(prev.waveId, prev.edge, prev.snapped);
        return null;
      });
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag?.waveId, drag?.edge, axis, timeline, onDragEdge]);

  const startDrag = (e, waveId, edge) => {
    e.preventDefault();
    e.stopPropagation();
    const pct = pctFromEvent(e.clientX);
    setDrag({ waveId, edge, pct, at: timeAt(axis, pct), snapped: null });
  };

  if (!axis) return null;

  const nowPct = axis.now ? pctOf(axis.now) : null;

  return (
    <div style={{ width: '100%' }}>
      {/* ---- Phase strip ---------------------------------------------------------------- */}
      <div
        style={{
          position: 'relative',
          height: STRIP_HEIGHT,
          width: '100%',
          borderRadius: 6,
          background: theme.GREY_10,
          overflow: 'hidden'
        }}
      >
        {bands.map((band) => {
          const left = pctOf(band.from);
          const right = pctOf(band.to);
          return (
            <div
              key={band.key}
              title={`${band.label} · ${fmtMaybe(band.from, band.observed)} → ${fmtMaybe(
                band.to,
                true
              )}`}
              style={{
                position: 'absolute',
                left: `${left}%`,
                width: `${Math.max(0, right - left)}%`,
                top: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                // The band's OPENING edge carries the certainty: solid where the match reached
                // it, dashed where it is still a projection off kick-off.
                borderLeft: band.event
                  ? `${band.observed ? '2px solid' : '2px dashed'} ${
                      band.observed ? theme.GREY_60 : theme.GREY_40
                    }`
                  : 'none',
                boxSizing: 'border-box',
                overflow: 'hidden'
              }}
            >
              <Text
                c1
                col={theme.GREY_60}
                marg="0"
                style={{
                  fontSize: 10,
                  whiteSpace: 'nowrap',
                  padding: '0 4px',
                  fontStyle: band.observed ? 'normal' : 'italic',
                  // A band narrower than its own label is normal (penalties is a few minutes on a
                  // two-hour axis). Bounding the label to the band and truncating the TAIL leaves
                  // a readable "Pena…"; without it the centred label overflowed both sides and the
                  // band showed the middle of the word. The tooltip carries the full name.
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  boxSizing: 'border-box'
                }}
              >
                {band.label}
              </Text>
            </div>
          );
        })}
      </div>

      {/* Boundary times, so a projected half-time reads as an estimate rather than a promise. */}
      <div style={{ position: 'relative', height: 16, width: '100%', marginTop: 2 }}>
        {boundaries.map((boundary) => (
          <Text
            key={boundary.event}
            c1
            col={boundary.observed ? theme.GREY_70 : theme.GREY_50}
            marg="0"
            title={
              boundary.coincident.length
                ? `${boundary.coincident
                    .map((e) => MATCH_EVENT_LABEL[e])
                    .join(' and ')} project to this same moment — nothing separates them until the match plays out.`
                : boundary.observed
                  ? 'Observed — the match reached this moment.'
                  : 'Projected from kick-off. This will move.'
            }
            style={{
              position: 'absolute',
              left: `${pctOf(boundary.at)}%`,
              transform: 'translateX(-50%)',
              fontSize: 9.5,
              whiteSpace: 'nowrap',
              fontStyle: boundary.observed ? 'normal' : 'italic'
            }}
          >
            {fmtMaybe(boundary.at, boundary.observed)}
          </Text>
        ))}
      </div>

      {/* ---- The one session track ------------------------------------------------------ */}
      <div
        ref={trackRef}
        style={{
          position: 'relative',
          height: TRACK_HEIGHT,
          width: '100%',
          marginTop: 6,
          borderRadius: 8,
          border: `1px solid ${theme.GREY_20}`,
          background: '#fff',
          // A dropped segment can never sit beside another: gaps come from the endpoint and the
          // waves are laid out from their resolved spans, so the row is the sequence itself.
          overflow: 'hidden',
          touchAction: 'none'
        }}
      >
        {/* Gaps first, so a wave segment always paints over them. */}
        {timeline.gaps.map((gap, i) => {
          const left = pctOf(gap.from);
          const width = Math.max(0, pctOf(gap.to) - left);
          return (
            <div
              key={`gap-${i}`}
              title={`No wave for ${fmtSpan(gap.minutes)} — the fan-facing card is dark here.`}
              style={{
                position: 'absolute',
                left: `${left}%`,
                width: `${width}%`,
                top: 5,
                bottom: 5,
                border: `1px dashed ${theme.GREY_40}`,
                borderRadius: 6,
                background:
                  'repeating-linear-gradient(45deg, rgba(148,163,184,0.08) 0 6px, transparent 6px 12px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
                boxSizing: 'border-box',
                overflow: 'hidden'
              }}
            >
              {!readOnly && onAddInGap ? (
                <button
                  type="button"
                  onClick={() => onAddInGap(gap)}
                  title={`Add a wave in this ${fmtSpan(gap.minutes)} gap`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 3,
                    border: `1px solid ${theme.GREY_30}`,
                    background: '#fff',
                    borderRadius: 20,
                    padding: '2px 8px',
                    cursor: 'pointer',
                    color: theme.GREY_70,
                    fontFamily: 'CeraPro',
                    fontSize: 10,
                    whiteSpace: 'nowrap'
                  }}
                >
                  <FiPlus size={10} />
                  {width > 8 ? `No wave · ${fmtSpan(gap.minutes)}` : 'Add'}
                </button>
              ) : (
                <Text c1 col={theme.GREY_50} marg="0" style={{ fontSize: 10, whiteSpace: 'nowrap' }}>
                  {width > 8 ? `No wave · ${fmtSpan(gap.minutes)}` : '—'}
                </Text>
              )}
            </div>
          );
        })}

        {timeline.waves.map((wave) => {
          const conflicts = timeline.conflictsByWave.get(wave.waveId) || [];
          const paint = SEGMENT_PAINT[segmentFill(wave)] || SEGMENT_PAINT.dashed;
          const clash = conflictPaintOf(conflicts);
          const isSelected = selectedWaveId === wave.waveId;
          const isActive = wave.status === WAVE_STATUS.ACTIVE;
          const terminal = isTerminalStatus(wave.status);

          const dragging = drag?.waveId === wave.waveId;
          const openPct =
            dragging && drag.edge === ANCHOR_EDGE.OPEN ? drag.pct : pctOf(wave.opensAt);
          const closePct =
            dragging && drag.edge === ANCHOR_EDGE.CLOSE ? drag.pct : pctOf(wave.closesAt);
          const left = Math.min(openPct, closePct);
          const width = Math.max(0.6, Math.abs(closePct - openPct));

          const anchors = anchorsFor ? anchorsFor(wave) : null;

          return (
            <div
              key={wave.waveId}
              onClick={() => onSelectWave && onSelectWave(wave.waveId)}
              title={`${fmtMaybe(wave.opensAt, !wave.pending)} → ${fmtMaybe(
                wave.closesAt,
                !wave.pending
              )}${wave.pending ? ' · projected, this will move' : ''}`}
              style={{
                position: 'absolute',
                left: `${left}%`,
                width: `${width}%`,
                top: 4,
                bottom: 4,
                borderRadius: 6,
                background: clash ? clash.bg : paint.bg,
                // Dashed where the wave has not run yet OR either edge is still projected: both
                // mean "this position is not final". A pending wave takes the contrasting border
                // so the dash is actually visible against its own fill.
                border: `${clash ? 2 : 1.5}px ${
                  paint.dash || wave.pending ? 'dashed' : 'solid'
                } ${clash ? clash.border : wave.pending ? paint.pendingBorder : paint.border}`,
                boxShadow: isSelected ? `0 0 0 2px ${theme.SECONDARY}` : 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxSizing: 'border-box',
                overflow: 'visible',
                opacity: terminal ? 0.75 : 1
              }}
            >
              <Text
                c1
                bold
                col={clash ? clash.col : paint.col}
                marg="0"
                style={{
                  fontSize: 10.5,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  padding: '0 8px',
                  pointerEvents: 'none'
                }}
              >
                {anchors
                  ? waveEdgesLabel(anchors, wave, fmtTime)
                  : `${fmtTime(wave.opensAt)} → ${fmtTime(wave.closesAt)}`}
              </Text>

              {!readOnly && !terminal && (
                <>
                  <Handle
                    side="open"
                    disabled={isActive}
                    onPointerDown={(e) => startDrag(e, wave.waveId, ANCHOR_EDGE.OPEN)}
                    title={
                      isActive
                        ? 'This wave is running — its start is locked because participation is anchored to it.'
                        : 'Drag to move the open. Near a phase boundary it snaps to that event.'
                    }
                  />
                  <Handle
                    side="close"
                    onPointerDown={(e) => startDrag(e, wave.waveId, ANCHOR_EDGE.CLOSE)}
                    title="Drag to move the close. Near a phase boundary it snaps to that event."
                  />
                </>
              )}
            </div>
          );
        })}

        {/* Playhead — only while the match is actually on the axis. */}
        {nowPct != null && (
          <div
            title={`Now · ${fmtTime(axis.now)}`}
            style={{
              position: 'absolute',
              left: `${nowPct}%`,
              top: 0,
              bottom: 0,
              width: 2,
              background: theme.SECONDARY,
              pointerEvents: 'none'
            }}
          >
            <span
              style={{
                position: 'absolute',
                top: -4,
                left: -3,
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: theme.SECONDARY
              }}
            />
          </div>
        )}
      </div>

      {/* What a drag currently reads as, before it is committed. */}
      {drag?.snapped && (
        <div
          style={{
            position: 'relative',
            height: 18,
            marginTop: 4,
            width: '100%'
          }}
        >
          <Text
            c1
            bold
            col={theme.SECONDARY}
            marg="0"
            style={{
              position: 'absolute',
              left: `${drag.pct}%`,
              transform: 'translateX(-50%)',
              fontSize: 10.5,
              whiteSpace: 'nowrap'
            }}
          >
            {anchorLabel(drag.snapped, fmtTime)}
          </Text>
        </div>
      )}

      {!readOnly && onAddAtEnd && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
          <button
            type="button"
            onClick={onAddAtEnd}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              border: `1px dashed ${theme.GREY_40}`,
              background: '#fff',
              borderRadius: 20,
              padding: '5px 12px',
              cursor: 'pointer',
              color: theme.GREY_70,
              fontFamily: 'CeraPro',
              fontSize: 11.5
            }}
          >
            <FiPlus size={12} />
            Add wave
          </button>
        </div>
      )}
    </div>
  );
};

export default SessionTrack;
